import axios from 'axios';
import type { IBaseConfig } from './IBaseConfig';

export interface IConfigServiceOptions {
    /** Базовый путь приложения (`VITE_BASE_PATH`): `config.json` лежит рядом с его index.html. */
    basePath: string;
}

/** Конфиг приложения: читается один раз до монтирования, дальше — синхронно. */
export interface IConfigService<T extends IBaseConfig> {
    /** Прочитать `config.json` (отказ сети — отказ промиса: без конфига приложение не стартует). */
    initAsync(): Promise<Readonly<T>>;
    /** Прочитанный конфиг; до `initAsync` — `undefined`. */
    get(): Readonly<T>;
}

/** Адрес `config.json`: относительный — `<base href="/">` index.html приводит его к корню приложения. */
export const configJsonUrl = (basePath: string): string =>
    basePath === '/' ? 'config.json' : `${basePath}/config.json`;

/**
 * Сервис конфига приложения. Экземпляр держит модуль приложения на прежнем пути
 * (`@/configuration/services/configService`): его мокают глобально в setup тестов, и там же стоит top-level await
 * чтения — ядро при импорте и создании ничего не читает. Объект изменяемый: истории Storybook подменяют `get`.
 */
export function createConfigService<T extends IBaseConfig>({ basePath }: IConfigServiceOptions): IConfigService<T> {
    let config: T | undefined;
    return {
        async initAsync() {
            console.debug(`Инициализация конфигурации`);
            const response = await axios.get<T>(configJsonUrl(basePath));
            config = response.data;
            return Object.freeze(config);
        },
        get() {
            return Object.freeze(config) as Readonly<T>;
        },
    };
}
